/* SKILL.md as the editor shows it: the name and the description as fields with their
   rules live, and the instructions with a preview beside them (or a switch, where there
   is no room). What the server says about either lands on the field it is about. */
import React, { useState } from "react";
import { Lock } from "lucide-react";

import { FieldLabel, TextArea, TextInput } from "@/shared/components/settings-atoms";
import { Segmented } from "@/shared/components/teams-ui";
import { DESCRIPTION_MAX, NAME_MAX, SKILL_CODES } from "@/shared/lib/skill-rules";

import { characters, descriptionIssue, nameIssue, type SkillMd } from "../folder";
import { ProblemLines } from "./atoms";
import SkillMarkdown from "./SkillMarkdown";

import type { SkillProblem } from "@/shared/types";

/* Which SKILL.md problems belong to which field (tasks/custom-skills/01-design-mock.md). */
const NAME_CODES: number[] = [SKILL_CODES.badName, SKILL_CODES.nameTaken];
const DESCRIPTION_CODES: number[] = [SKILL_CODES.badDescription];
const FRONTMATTER_CODES: number[] = [SKILL_CODES.badFrontmatter];

/* A field the rules refuse keeps its danger ring while focused too. (Each field's 16px type
   below `sm` is so iOS doesn't zoom into it.) */
const invalid = "!ring-[var(--danger-ring)]";

function Counter({ n, max }: { n: number; max: number }) {
  return (
    <span className={`shrink-0 font-mono tabular-nums text-[11.5px] ${n > max ? "text-[var(--danger)]" : "text-[var(--text-quaternary)]"}`}>
      {n.toLocaleString()} / {max.toLocaleString()}
    </span>
  );
}

function Hint({ issue, hint }: { issue: string | null; hint: string }) {
  return <span className={`min-w-0 text-[12px] leading-[1.5] ${issue ? "text-[var(--danger)]" : "text-[var(--text-tertiary)]"}`}>{issue ?? hint}</span>;
}

export default function SkillMdFields({ md, onChange, nameFixed, problems, showIssues }: {
  md: SkillMd;
  onChange: (md: SkillMd) => void;
  /** Editing: the name can't change, because agents call a skill by it. */
  nameFixed: boolean;
  /** The server's problems with SKILL.md. */
  problems: SkillProblem[];
  /** After a save was tried, empty fields say so too. */
  showIssues: boolean;
}) {
  const [view, setView] = useState<"write" | "preview">("write");
  const set = (patch: Partial<SkillMd>) => onChange({ ...md, ...patch });
  const nameProblems = problems.filter((p) => NAME_CODES.includes(p.code));
  const descriptionProblems = problems.filter((p) => DESCRIPTION_CODES.includes(p.code));
  const frontmatterProblems = problems.filter((p) => FRONTMATTER_CODES.includes(p.code));
  const otherProblems = problems.filter((p) => ![...NAME_CODES, ...DESCRIPTION_CODES, ...FRONTMATTER_CODES].includes(p.code));
  const nameLive = md.name || showIssues ? nameIssue(md.name) : null;
  const descriptionLive = md.description || showIssues ? descriptionIssue(md.description) : null;

  return (
    <div className="max-w-[960px] mx-auto px-4 md:px-6 py-6 space-y-5">
      <div>
        <FieldLabel>Name</FieldLabel>
        <div className="relative">
          <TextInput
            value={md.name}
            readOnly={nameFixed}
            onChange={(name) => set({ name })}
            aria-label="Name"
            aria-invalid={!!nameLive || nameProblems.length > 0}
            placeholder="release-notes"
            spellCheck={false}
            className={`font-mono text-[16px] sm:text-[13px] ${nameFixed ? "pr-9 bg-[var(--surface-sunk)] text-[var(--text-secondary)]" : ""} ${nameLive || nameProblems.length ? invalid : ""}`}
          />
          {nameFixed && <Lock className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[var(--text-quaternary)]" strokeWidth={1.75} />}
        </div>
        <div className="mt-1.5 flex items-start justify-between gap-3">
          <Hint
            issue={nameFixed ? null : nameLive}
            hint={nameFixed ? "Fixed once a skill exists: agents call a skill by its name." : "Lowercase letters, digits and hyphens. Agents call the skill by it."}
          />
          {!nameFixed && <Counter n={md.name.length} max={NAME_MAX} />}
        </div>
        <ProblemLines problems={nameProblems} />
      </div>

      <div>
        <FieldLabel>Description</FieldLabel>
        <TextArea
          value={md.description}
          onChange={(description) => set({ description })}
          rows={3}
          aria-label="Description"
          aria-invalid={!!descriptionLive || descriptionProblems.length > 0}
          placeholder="What it does, and when to use it."
          className={`resize-y text-[16px] sm:text-[13.5px] ${descriptionLive || descriptionProblems.length ? invalid : ""}`}
        />
        <div className="mt-1 flex items-start justify-between gap-3">
          <Hint issue={descriptionLive} hint="The agent picks a skill by this alone: say what it does and when to use it." />
          <Counter n={characters(md.description)} max={DESCRIPTION_MAX} />
        </div>
        <ProblemLines problems={descriptionProblems} />
      </div>

      <div>
        <FieldLabel>Other frontmatter</FieldLabel>
        <TextArea
          value={md.extra}
          onChange={(extra) => set({ extra })}
          rows={Math.min(8, Math.max(2, md.extra.split("\n").length))}
          aria-label="Other frontmatter"
          aria-invalid={frontmatterProblems.length > 0}
          placeholder="license: MIT"
          spellCheck={false}
          className={`resize-y font-mono text-[16px] sm:text-[12.5px] leading-[1.6] ${frontmatterProblems.length ? invalid : ""}`}
        />
        <div className="mt-1">
          <Hint issue={null} hint="Optional YAML lines beside the name and description: license, metadata, allowed-tools." />
        </div>
        <ProblemLines problems={frontmatterProblems} />
      </div>

      <div>
        <div className="flex items-center justify-between gap-3 mb-1.5">
          <FieldLabel className="mb-0">Instructions</FieldLabel>
          <Segmented
            value={view}
            onChange={setView}
            options={[{ value: "write", label: "Write" }, { value: "preview", label: "Preview" }]}
            className="xl:hidden"
          />
        </div>
        <ProblemLines problems={otherProblems} />
        <div className="flex gap-3 min-w-0">
          <TextArea
            value={md.body}
            onChange={(body) => set({ body })}
            aria-label="Instructions"
            spellCheck={false}
            className={`${view === "preview" ? "hidden xl:block" : ""} flex-1 min-w-0 min-h-[420px] resize-y px-3.5 py-3 font-mono text-[16px] sm:text-[12.5px] leading-[1.65]`}
          />
          <div
            aria-label="Preview"
            className={`${view === "write" ? "hidden xl:block" : ""} flex-1 min-w-0 min-h-[420px] rounded-[10px] bg-[var(--surface-sunk)] ring-1 ring-[var(--hairline)] px-4 py-3 overflow-x-hidden`}
          >
            {md.body.trim() ? <SkillMarkdown text={md.body} /> : <span className="text-[11.5px] text-[var(--text-quaternary)] italic">Nothing to preview yet.</span>}
          </div>
        </div>
      </div>
    </div>
  );
}
